import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./services/axios";

const METODOS = [
  { value: "card", label: "Cartão" },
  { value: "pix", label: "Pix" }
];

export default function PainelAdmin() {
  const navigate = useNavigate();

  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [valor, setValor] = useState("");
  const [parcelas, setParcelas] = useState(1);
  const [metodo, setMetodo] = useState("card");
  const [cobrancas, setCobrancas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Opções de parcelas recalculadas dinamicamente com o valor digitado
  const gerarParcelasOpcoes = Array.from({ length: 12 }, (_, i) => {
    const qtd = i + 1;
    const valorParcela = valor && !isNaN(valor) && parseFloat(valor) > 0
      ? (parseFloat(valor) / qtd).toFixed(2)
      : null;
    return {
      value: qtd,
      label: valorParcela
        ? `Até ${qtd}x de R$ ${valorParcela.replace('.', ',')} sem juros`
        : `Até ${qtd}x sem juros`
    };
  });

  const camposValidos =
    nome.trim() &&
    cpf.replace(/\D/g, "").length === 11 &&
    telefone.replace(/\D/g, "").length >= 10 &&
    /\S+@\S+\.\S+/.test(email) &&
    parseFloat(valor) > 0;

  const handleErroAuth = (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const criarCobranca = async () => {
    setLoading(true);
    try {
      const response = await api.post("/criar-cobranca", {
        nome,
        cpf: cpf.replace(/\D/g, ""),
        telefone: telefone.replace(/\D/g, ""),
        email,
        valor: parseFloat(valor),
        parcelas,
        metodo
      });

      setNome("");
      setCpf("");
      setTelefone("");
      setEmail("");
      setValor("");
      setParcelas(1);
      setMetodo("card");

      fetchCobrancas();

      const mensagem = gerarMensagem(response.data);
      navigator.clipboard.writeText(mensagem);
      alert("Cobrança criada e mensagem copiada para a área de transferência!");
    } catch (err) {
      handleErroAuth(err);
      alert("Erro ao gerar cobrança: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchCobrancas = async () => {
    try {
      const resp = await api.get("/cobrancas");
      setCobrancas(resp.data);
    } catch (err) {
      handleErroAuth(err);
      console.error("Erro ao buscar cobranças:", err);
    }
  };

  useEffect(() => {
    fetchCobrancas();
    const interval = setInterval(() => {
      api.post("/atualizar-status")
        .then(res => setCobrancas(res.data))
        .catch(err => {
          if (err.response?.status === 401) {
            sessionStorage.removeItem("token");
            window.location.href = "/login";
          }
        });
    }, 2000); // 2 segundos, pode ajustar
    return () => clearInterval(interval);
  }, []);

  const gerarMensagem = (c) => {
    let msg = `Cobrança para: ${c.nome}
CPF: ${c.cpf}
Telefone: ${c.telefone}
Valor: R$ ${Number(c.valor).toFixed(2)}
Parcelamento: até ${c.parcelas}x sem juros
Modalidade: ${c.metodo === "pix" ? "Pix" : "Cartão"}

Acesse o link abaixo para pagamento (só será redirecionado após aceite do termo):

link de pagamento: ${window.location.origin}/pagamento/${c.idPagamento || c.id}`;
    if (c.metodo === "pix" && c.qr_code) {
      msg += `\n\nPix Copia e Cola:\n${c.qr_code}`;
    }
    return msg;
  };

  const copiarMensagem = (c) => {
    navigator.clipboard.writeText(gerarMensagem(c));
    alert("Mensagem copiada!");
  };

  const copiarPix = (pix) => {
    if (pix) {
      navigator.clipboard.writeText(pix);
      alert("Pix Copia e Cola copiado!");
    } else {
      alert("Não há código PIX disponível");
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "approved": return "Pago";
      case "pending": return "Aguardando";
      case "rejected": return "Rejeitado";
      case "in_process": return "Processando";
      case "cancelled": return "Cancelado";
      case "refunded": return "Estornado";
      default: return status || "Aguardando";
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "approved": return "bg-green-200 text-green-800";
      case "pending": return "bg-yellow-200 text-yellow-800";
      case "rejected": return "bg-red-200 text-red-800";
      case "in_process": return "bg-blue-200 text-blue-800";
      case "cancelled": return "bg-gray-200 text-gray-800";
      case "refunded": return "bg-purple-200 text-purple-800";
      default: return "bg-yellow-200 text-yellow-800";
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-900 via-blue-900 to-gray-900 px-2 py-4">
      <div className="w-full max-w-md sm:max-w-lg lg:max-w-4xl bg-white/90 rounded-2xl shadow-2xl p-4 sm:p-8 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl sm:text-3xl font-extrabold text-indigo-800 tracking-tight drop-shadow-lg">
            Painel de Cobrança
          </h1>
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition text-xs sm:text-sm"
          >
            Logout
          </button>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              placeholder="Nome completo"
              value={nome}
              onChange={e => setNome(e.target.value)}
            />
            <input
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              placeholder="CPF (somente números)"
              maxLength={11}
              type="text"
              value={cpf}
              onChange={e => setCpf(e.target.value.replace(/\D/g, ""))}
            />
            <input
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              placeholder="Telefone (DDD + número)"
              maxLength={11}
              type="tel"
              value={telefone}
              onChange={e => setTelefone(e.target.value.replace(/\D/g, ""))}
            />
            <input
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              placeholder="E-mail do cliente"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              placeholder="Valor total (ex: 199.90)"
              type="number"
              step="0.01"
              value={valor}
              onChange={e => setValor(e.target.value)}
            />
            <select
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              value={parcelas}
              onChange={e => setParcelas(Number(e.target.value))}
              disabled={metodo === "pix"}
            >
              {gerarParcelasOpcoes.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
            <select
              className="w-full border border-indigo-200 rounded-xl px-4 py-3 text-base sm:text-lg focus:ring-2 focus:ring-indigo-400 bg-gray-50 shadow-sm"
              value={metodo}
              onChange={e => setMetodo(e.target.value)}
            >
              {METODOS.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>

          <div className="bg-yellow-100 text-yellow-900 rounded-lg px-4 py-2 text-xs border border-yellow-200">
            O link enviado ao cliente conterá um termo de aceite obrigatório antes do pagamento. Todos os dados serão registrados conforme LGPD.
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              className="flex-1 bg-indigo-700 hover:bg-indigo-800 text-white font-bold rounded-xl py-3 text-base sm:text-lg shadow-md transition disabled:opacity-50"
              onClick={criarCobranca}
              disabled={loading || !camposValidos}
            >
              {loading ? "Gerando..." : "Gerar link de pagamento"}
            </button>
          </div>
        </div>

        <hr className="my-6 sm:my-8" />

        <h2 className="text-lg sm:text-xl font-bold text-gray-700 mb-4">Cobranças geradas</h2>
        <div className="flex flex-col gap-3 max-h-96 overflow-y-auto pr-2">
          {cobrancas.length === 0 ? (
            <span className="text-gray-400 text-center py-4">Nenhuma cobrança gerada ainda.</span>
          ) : (
            cobrancas.map(c => (
              <div key={c.idPagamento || c.id} className="border border-indigo-100 rounded-xl p-4 bg-gray-50 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-800 text-base">{c.nome}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusColor(c.status)}`}>
                        {statusLabel(c.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">CPF: </span>
                        <span>{c.cpf}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Tel: </span>
                        <span>{c.telefone}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Email: </span>
                        <span className="break-all">{c.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Parcelas: </span>
                        <span>até {c.parcelas}x</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Valor: </span>
                        <span className="text-blue-700 font-bold">R$ {Number(c.valor).toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Modalidade: </span>
                        <span className="font-bold">{c.metodo === "pix" ? "Pix" : "Cartão"}</span>
                      </div>
                    </div>

                    {c.status === "approved" && c.pagamento_id && (
                      <div className="mt-3">
                        <a
                          href={`/comprovante?payment_id=${c.pagamento_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 text-sm font-bold hover:underline"
                        >
                          ✅ Ver comprovante
                        </a>
                      </div>
                    )}

                    {c.metodo === "pix" && c.qr_code && (
                      <div className="mt-3">
                        <span className="font-bold text-xs">Pix Copia e Cola:</span>
                        <div className="flex items-start gap-2 mt-1">
                          <span className="flex-1 text-xs bg-gray-200 rounded px-2 py-1 break-all">
                            {c.qr_code}
                          </span>
                          <button
                            className="px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition"
                            onClick={() => copiarPix(c.qr_code)}
                          >
                            Copiar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row md:flex-col gap-2">
                    <a
                      href={`${window.location.origin}/pagamento/${c.idPagamento || c.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-center text-xs sm:text-sm text-indigo-700 underline font-medium hover:text-indigo-900 whitespace-nowrap"
                    >
                      Link para cliente
                    </a>
                    <button
                      className="px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition"
                      onClick={() => copiarMensagem(c)}
                    >
                      Copiar mensagem
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
